import jsPDF from 'jspdf';
import { Storyboard, CIAdDetail } from './types';
import { formatEnum, wrapText, sanitizePdfText, imageFormatFromDataUrl } from './pdfHelpers';
import { groupShotsIntoClips, clipSpecs, formatShotRole } from './clipView';

// Wrap a value in curly quotes, demoting any quotes already inside it to
// single quotes — a title that arrives quoted otherwise renders as ""cooling.""
function quoted(text: string): string {
  const inner = text.trim().replace(/^["“](.*)["”]$/s, '$1');
  let open = true;
  return `“${inner.replace(/"/g, () => (open = !open) ? '’' : '‘')}”`;
}

// A keyframe image already resolved to an embeddable base64 data URL (the
// backend reads the Spaces object; the browser can't fetch it cross-origin).
export interface ScriptPdfKeyframe {
  shotOrder: number;
  startTime: number | null;
  dataUrl: string;
  // Natural pixel dimensions, used to preserve aspect ratio in the PDF.
  width: number;
  height: number;
}

// A reference ad bundled with its (already-fetched) transcript + keyframe images.
export interface ScriptPdfReference {
  ad: CIAdDetail;
  transcript: string | null;
  keyframes: ScriptPdfKeyframe[];
}

// Which sections to include, and the (human-editable) script text to print.
export interface ScriptPdfOptions {
  scriptText: string;
  include: {
    script: boolean;
    videoLink: boolean;
    keyframes: boolean;
    transcript: boolean;
    shotByShot: boolean;
  };
}

// "Creator Brief" PDF for a finished generated script — for a UGC creator to
// perform/film. Leads with the (editable) script, then the original reference
// (video link + keyframes + transcript), then an optional shot-by-shot
// breakdown. Sections are toggled via opts.include.
async function buildScriptPdfDoc(
  storyboard: Storyboard,
  productName: string | null,
  references: ScriptPdfReference[],
  opts: ScriptPdfOptions,
): Promise<jsPDF> {
  const { scriptText, include } = opts;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkPageBreak = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };
  const drawDivider = () => {
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
  };
  let firstSection = true;
  const sectionTitle = (title: string, opts: { divider?: boolean } = {}) => {
    const { divider = true } = opts;
    if (!firstSection && divider) drawDivider();
    firstSection = false;
    checkPageBreak(14);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(title, margin, y);
    y += 7;
  };
  const bodyText = (text: string, o: { size?: number; indent?: number; lineH?: number; italic?: boolean } = {}) => {
    const { size = 10, indent = 0, lineH = 4.5, italic = false } = o;
    doc.setFontSize(size);
    doc.setFont('helvetica', italic ? 'italic' : 'normal');
    doc.setTextColor(0);
    for (const line of wrapText(doc, text, contentWidth - indent)) {
      checkPageBreak(lineH);
      doc.text(line, margin + indent, y);
      y += lineH;
    }
  };

  // ── Header ──
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Creator Brief', margin, y);
  y += 8;
  doc.setFontSize(13);
  doc.text(sanitizePdfText(productName || `Script ${storyboard.storyboardId.slice(0, 8)}`), margin, y);
  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  const duration = storyboard.totalDurationSec || storyboard.targetDurationSec;
  const metaParts: string[] = [];
  if (duration) metaParts.push(`Target length: ~${duration}s`);
  metaParts.push(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`);
  doc.text(metaParts.join('  |  '), margin, y);
  doc.setTextColor(0);
  y += 8;

  // ── How to use this brief (creator guidance) ──
  // The brief is a starting point to recreate in the creator's own voice, not a
  // line to copy — point them at the reference video as the guide.
  bodyText(
    'How to use this brief: film your own take on the script below, using the reference video as your guide for pacing, energy, and overall structure. You don’t need to match it word-for-word — keep the same vibe, but make it feel natural and true to you.',
    { size: 10, italic: true, lineH: 5 },
  );
  y += 6;

  // ── Script (editable) ──
  if (include.script) {
    sectionTitle('Script');
    if (scriptText && scriptText.trim()) {
      bodyText(scriptText, { size: 11, lineH: 5 });
    } else {
      bodyText('(no script text)', { size: 10, italic: true });
    }
    y += 4;
  }

  // ── Reference ──
  const wantReference = include.videoLink || include.keyframes || include.transcript;
  if (wantReference && references.length > 0) {
    if (!firstSection) y += 9; // ~2-line gap before Reference
    sectionTitle('Reference', { divider: false }); // no divider line above the header
    for (const ref of references) {
      const { ad, transcript, keyframes } = ref;
      checkPageBreak(12);
      if (references.length > 1) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(sanitizePdfText(ad.pageName || ad.headline || ad.adId), margin, y);
        y += 6;
      }

      if (include.videoLink) {
        const videoLink = ad.archivedVideoShareUrl || ad.videoUrl;
        if (videoLink) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0);
          doc.text('Watch the original:', margin, y);
          y += 4;
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(40, 80, 160);
          for (const line of wrapText(doc, videoLink, contentWidth)) {
            checkPageBreak(4);
            // Attach the FULL url as a real hyperlink on every wrapped line. The
            // URL wraps across lines, and without an explicit link annotation a
            // PDF viewer only auto-detects the first line's text — opening a
            // truncated link. textWithLink makes each line open the whole URL.
            doc.textWithLink(line, margin, y, { url: videoLink });
            y += 4;
          }
          doc.setTextColor(0);
          y += 3;
        }
      }

      if (include.keyframes && keyframes.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        checkPageBreak(6);
        doc.text('Keyframes:', margin, y);
        y += 5;
        const perRow = 3; // 3-up on the dedicated Reference page
        const gap = 5;
        const cellW = (contentWidth - gap * (perRow - 1)) / perRow;
        const maxImgH = cellW * 1.4; // allow tall portrait frames, capped
        // Render row by row; each row is sized to its tallest frame so the real
        // aspect ratio is preserved with no wasted vertical space.
        for (let i = 0; i < keyframes.length; i += perRow) {
          const rowItems = keyframes.slice(i, i + perRow);
          const draws = rowItems.map(kf => {
            const ok = kf.dataUrl && kf.width > 0 && kf.height > 0;
            const scale = ok ? Math.min(cellW / kf.width, maxImgH / kf.height) : 1;
            return { kf, drawW: ok ? kf.width * scale : cellW, drawH: ok ? kf.height * scale : cellW * 0.5625 };
          });
          const rowImgH = Math.max(...draws.map(d => d.drawH));
          checkPageBreak(rowImgH + 6);
          draws.forEach((d, col) => {
            const cellX = margin + col * (cellW + gap);
            const imgX = cellX + (cellW - d.drawW) / 2; // center horizontally in the cell
            const imgY = y + (rowImgH - d.drawH) / 2;   // center vertically in the row band
            if (d.kf.dataUrl) {
              try { doc.addImage(d.kf.dataUrl, imageFormatFromDataUrl(d.kf.dataUrl), imgX, imgY, d.drawW, d.drawH); } catch { /* skip bad image */ }
            }
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(120);
            const label = `#${d.kf.shotOrder}${typeof d.kf.startTime === 'number' ? ` · ${d.kf.startTime.toFixed(1)}s` : ''}`;
            doc.text(label, cellX, y + rowImgH + 3);
            doc.setTextColor(0);
          });
          y += rowImgH + 6;
        }
        y += 3;
      }

      if (include.transcript && transcript && transcript.trim()) {
        y += 4.5; // one-line gap between the keyframes and the transcript
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        checkPageBreak(8);
        doc.text('Original transcript:', margin, y);
        y += 5;
        bodyText(transcript.trim(), { size: 9, lineH: 4 });
        y += 3;
      }
    }
  }

  // ── Shot-by-shot ──
  const segments = (storyboard.segments || []).slice().sort((a, b) => a.segmentOrder - b.segmentOrder);
  if (include.shotByShot && segments.length > 0) {
    sectionTitle('Shot-by-shot');
    // The freedom note frames the per-cut specs below: structure is prescribed,
    // delivery is the creator's. Only shown when at least one segment carries
    // cut-level specs (older storyboards have none).
    if (segments.some(s => (s.shots || []).length > 0)) {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(90);
      const note = 'Film each clip below as one continuous, natural take: run through its actions in order within the rough length, reading the suggested script as you go — your own phrasing and setting are welcome. We do all cutting and transitions in the edit, so don’t stop between actions.';
      for (const noteLine of wrapText(doc, note, contentWidth)) {
        checkPageBreak(4.5);
        doc.text(noteLine, margin, y);
        y += 4.5;
      }
      // The upload-naming rule is the one instruction that breaks the edit if
      // it's missed, so it gets its own bold line rather than trailing the
      // paragraph above.
      y += 1.5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(20);
      const naming = 'When you upload to the Google Drive folder, name each file exactly as its clip name (e.g. "2-open-the-bag") — or at least start the filename with the clip number.';
      for (const namingLine of wrapText(doc, naming, contentWidth)) {
        checkPageBreak(4.5);
        doc.text(namingLine, margin, y);
        y += 4.5;
      }
      doc.setTextColor(0);
      y += 3;
    }
    segments.forEach((seg, si) => {
      if (si > 0) y += 3;
      // Keep a segment header with the start of its first clip — a banded
      // header alone at the foot of a page reads as a dead end.
      checkPageBreak(36);
      // Segment header: a light banded strip, so the eye can find where one
      // section of the ad ends and the next begins without reading labels.
      doc.setFillColor(238, 240, 244);
      doc.rect(margin, y - 4, contentWidth, 7, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30);
      const dur = seg.durationSec ? ` · ~${seg.durationSec}s` : '';
      doc.text(`${seg.segmentOrder} · ${formatEnum(seg.segmentRole)}${dur}`, margin + 2, y);
      y += 7;
      // A labelled body line. Labels sit in a fixed-width gutter so every
      // value in a clip starts at the same x — the ragged label-width
      // indentation was what made the old layout read as noise.
      // A labelled body line, or several values stacked under one label. The
      // label sits in a fixed-width gutter so every value in a clip starts at
      // the same x — ragged label-width indentation is what made the old
      // layout read as noise. Multiple values become separate bulleted lines
      // rather than one run-on (and the arrow separator we used before was
      // silently dropped: jsPDF's Helvetica has no glyph for it).
      const line = (label: string, val: string | string[] | null, o: { indent?: number; gutter?: number } = {}) => {
        const values = (Array.isArray(val) ? val : [val])
          .map(v => (v || '').trim())
          .filter(Boolean);
        if (values.length === 0) return;
        const { indent = 6, gutter = 46 } = o;
        const bulleted = values.length > 1;
        doc.setFontSize(9);
        checkPageBreak(5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(120);
        doc.text(label, margin + indent, y);
        // Never let a long label collide with its value, even if the gutter
        // is too narrow for it.
        const textX = margin + indent + Math.max(gutter, doc.getTextWidth(label) + 3);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(20);
        const hangX = bulleted ? textX + 3.2 : textX;
        values.forEach((value, vi) => {
          if (vi > 0) checkPageBreak(5);
          const lines = wrapText(doc, value, pageWidth - margin - hangX);
          if (bulleted) doc.text('•', textX, y);
          // Wrapped continuations hang under the first line's text, clear of
          // the bullet.
          lines.forEach((text, li) => {
            if (li > 0) checkPageBreak(5);
            doc.text(text, hangX, y);
            y += 5;
          });
        });
      };
      // Creator-facing: one entry per continuous clip — merged length, the
      // actions to hit in order, the suggested lines said once. The internal
      // cut plan stays in the data; cutting and transitions happen in the
      // edit. The clip lines carry everything, so the segment-level
      // SAY/SHOW/SHOT/TEXT fields print only on old storyboards without them.
      const clips = groupShotsIntoClips(seg.shots);
      if (clips) {
        clips.forEach((clip, ci) => {
          y += ci > 0 ? 7 : 2;
          // Clip header: the filename in bold (it IS the instruction — the
          // creator types it into Drive), specs in muted grey beside it, and
          // a rule under it so each clip reads as its own block. Keep the
          // header with at least its first two body lines so a clip never
          // starts at the bottom of a page.
          checkPageBreak(20);
          doc.setFontSize(9.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(20);
          doc.text(clip.clipName, margin + 4, y);
          const nameW = doc.getTextWidth(clip.clipName);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(130);
          doc.text(clipSpecs(clip).join(' · '), margin + 4 + nameW + 3, y);
          y += 2.5;
          doc.setDrawColor(226);
          doc.line(margin + 4, y, pageWidth - margin, y);
          y += 5;
          doc.setTextColor(0);
          if (clip.title) line('Title on screen', quoted(clip.title));
          if (clip.actions.length > 0) line('Suggested take', clip.actions);
          line('Suggested script to read', clip.suggested ? quoted(clip.suggested) : '(film silent)');
        });
        y += 2;
      } else {
        line('Say', seg.scriptText);
        line('Show', seg.visualDirection);
        line('Shot', seg.shotType);
        line('On screen', seg.onScreenText);
        // Old storyboards without clip names: per-cut fallback.
        (seg.shots || []).forEach((shot, i) => {
          const specs = [
            shot.durationSec != null ? `~${shot.durationSec}s` : null,
            shot.framing ? formatEnum(shot.framing) : null,
            shot.shotRole ? formatShotRole(shot.shotRole) : null,
            shot.cameraMovement ? formatEnum(shot.cameraMovement) : null,
          ].filter(Boolean).join(' · ');
          const desc = shot.description ? ` — ${shot.description}` : '';
          const spoken = shot.line ? ` · Suggested script to read: "${shot.line}"` : '';
          const title = shot.onScreenText ? ` · Title on screen: "${shot.onScreenText}"` : '';
          line(`Cut ${i + 1}`, `${specs}${desc}${spoken}${title}`);
        });
      }
      // Segment-wide, not part of the clip above it — set off by a gap and
      // pulled left to the segment's own indent.
      if (seg.brollNotes && seg.brollNotes.trim()) {
        y += 3;
        line('Extra B-Roll', seg.brollNotes, { indent: 2, gutter: 48 });
      }
      y += 2;
    });
  }

  // ── Footer ──
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(160);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    doc.text(`Script ID: ${storyboard.storyboardId}`, margin, pageHeight - 8);
  }

  return doc;
}

// Shared filename: "<product-slug>-creator-brief-<date>.pdf".
export function scriptPdfFilename(productName: string | null): string {
  const slug = (productName || 'script').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  const date = new Date().toISOString().slice(0, 10);
  return `${slug}-creator-brief-${date}.pdf`;
}

// Save the brief as a download (existing behavior).
export default async function generateScriptPdf(
  storyboard: Storyboard,
  productName: string | null,
  references: ScriptPdfReference[],
  opts: ScriptPdfOptions,
): Promise<void> {
  const doc = await buildScriptPdfDoc(storyboard, productName, references, opts);
  doc.save(scriptPdfFilename(productName));
}

// Same brief as a Blob, for sending to the backend (e.g. attach to a Trello card).
export async function generateScriptPdfBlob(
  storyboard: Storyboard,
  productName: string | null,
  references: ScriptPdfReference[],
  opts: ScriptPdfOptions,
): Promise<Blob> {
  const doc = await buildScriptPdfDoc(storyboard, productName, references, opts);
  return doc.output('blob');
}
